import React, { useState, createContext, useEffect, useContext } from 'react'
import {
  User,
  SignupCredentials,
  SignupInviteCredentials,
  SigninCredentials,
  Tokens,
  Feature,
  AuthEvent,
} from '../types'
import { AuthService } from '../services/auth.service'
import { tokensStorage, userStorage, allowedFeaturesStorage } from '../helper/storage'
import { getRefreshTokenHttpInterceptor } from '../interceptors'
import { mainNotificationContext } from 'app/modules/shared/context/main-notification-context'
import { HttpService, applicationConfiguration } from 'app/modules/shared'
import { ApplicationConfiguration } from 'app/modules/shared/types'
import { ExternalAuthType } from 'app/modules/admin/features/settings/auth-settings/types'
import { checkUrlPathMatchesPagePath, isInIframe, trackEvent } from 'app/modules/shared/helpers'
import { traceOtelAuthEvent } from 'app/modules/shared/context/monitoring/otel'
import { logger } from 'app/components'

// Helper function to get Okta user ID from localStorage/sessionStorage
// This function was requested as it is, do not touch its implementation
function getOktaUserSub(): string | undefined {
  try {
    // Common storage key for okta-auth-js; check both stores
    const raw = localStorage.getItem('okta-token-storage') ?? sessionStorage.getItem('okta-token-storage')
    if (!raw) return undefined

    const store = JSON.parse(raw)

    // Shapes vary by SDK version; try claims first
    const idt = store.idToken ?? store.tokens?.idToken
    let claims = idt?.claims ?? null

    if (!claims && idt && idt.idToken) {
      // Fallback: decode JWT
      const jwt = idt.idToken.split('.')[1]
      claims = JSON.parse(atob(jwt.replace(/-/g, '+').replace(/_/g, '/')))
    }

    return claims?.sub ? String(claims.sub) : undefined
  } catch (e) {
    logger.error('Error extracting Okta user sub:', e)
    return undefined
  }
}

export interface AuthContextData {
  user?: User
  getUser: () => Promise<User>
  signup: (credentials: SignupCredentials) => Promise<void>
  signupInvite: (credentials: SignupInviteCredentials) => Promise<void>
  signupFirstUser: (credentials: SignupCredentials) => Promise<void>
  confirmSignup: (token: string) => Promise<User>
  resendConfirmLink: (email: string) => Promise<void>
  signin: (credentials: SigninCredentials) => Promise<User>
  signout: () => Promise<void>
  clearUserState: () => void
  requestPasswordReset: (email: string) => Promise<void>
  validatePasswordResetToken: (token: string) => Promise<void>
  resetPassword: (token: string, password: string, password_confirm: string) => Promise<void>
  refreshTokens: () => Promise<void>
  finishExternalSignin: (
    authType: ExternalAuthType,
    service: string,
    code: string,
    state: string | undefined
  ) => Promise<User>
}

export const AuthContext = createContext<AuthContextData>({} as AuthContextData)

/**
 * Context provider for authentification hook
 */
export function WithAuthContext({ children }: { children: JSX.Element | JSX.Element[] }): JSX.Element | null {
  const mainNotification = useContext(mainNotificationContext)
  const config = useContext(applicationConfiguration)
  const [authChecked, setAuthChecked] = useState(false)
  const [user, setUser] = useState<User | undefined>(userStorage.getUser())
  const auth = getAuthMethods(user, setUser, config)

  useEffect(() => {
    const [interceptor, onError] = getRefreshTokenHttpInterceptor(auth, mainNotification, config)
    HttpService.addResponseInterceptor(interceptor, onError)
  }, [])

  // Check auth state on initial page load
  useEffect(() => {
    const pathname = location.pathname
    const accessToken = tokensStorage.getAccessToken()
    const clearAuthState = isInIframe() && checkUrlPathMatchesPagePath(pathname, 'external-auth-redirect')

    logger.debug('Determine if we need to clear auth state on page load: ', clearAuthState)

    if (clearAuthState || !accessToken) {
      // To be 100% correct, we should reload config here after clearing access tokens.
      // But for now, as clearing access token is only crucial for external authentication
      // redirect page ,this should be skipped. If we reload config here, it causes
      // duplication of last signin request on external signin redirect page.
      // In case signin is successfull, config will be reloaded right after it.
      // In case it is not, it is reloaded right in external signin page controller.
      // So we can just skip it here.

      clearAuthState && auth.clearUserState()
      setAuthChecked(true)

      return
    }

    async function updateUser() {
      try {
        await auth.getUser()
      } catch (error) {
        console.error('Error while updating user info on page load, so forcing signout')
        console.error(error)

        auth.signout()
      }

      setAuthChecked(true)
    }

    updateUser()
    // eslint-disable-next-line
  }, [])

  return authChecked ? <AuthContext.Provider value={auth}>{children}</AuthContext.Provider> : null
}

/**
 * Methods and data dealing with authentication.
 */
function getAuthMethods(
  user: User | undefined,
  setUser: CallableFunction,
  config: ApplicationConfiguration
): AuthContextData {
  /**
   * Perform new user signup
   */
  async function signup(credentials: SignupCredentials) {
    await AuthService.signup(credentials)
  }

  /**
   * Perform first app user signup
   */
  async function signupFirstUser(credentials: SignupCredentials) {
    const tokens = await AuthService.signupGetTokens(credentials)
    saveTokens(tokens)
  }

  /**
   * Perform signup by invitation
   */
  async function signupInvite(credentials: SignupInviteCredentials) {
    const tokens = await AuthService.signupInviteGetTokens(credentials)
    saveTokens(tokens)
    traceOtelAuthEvent(AuthEvent.SignupSuccess)
  }

  /**
   * Confirm new user signup
   */
  async function confirmSignup(token: string) {
    const tokens = await AuthService.confirmSignup(token)
    !!tokens && saveTokens(tokens)

    return await getUser()
  }

  /**
   * Request the repeating of sending of signup confirmation link
   */
  async function resendConfirmLink(token: string) {
    return await AuthService.resendConfirmLink(token)
  }

  /**
   * Authenticate registered user
   */
  async function signin(credentials: SigninCredentials) {
    const tokens = await AuthService.signin(credentials)
    saveTokens(tokens)

    const user = await getUser()

    // Track login success in GTM dataLayer
    if (user) {
      trackEvent({
        event: AuthEvent.SigninSuccess,
        user_id: user.userId,
      })
      traceOtelAuthEvent(AuthEvent.SigninSuccess, user.userId)
    }

    return user
  }

  /**
   * Complete OAuth authentication
   */
  async function finishExternalSignin(
    authType: ExternalAuthType,
    service: string,
    code: string,
    state: string | undefined
  ) {
    const tokens = await AuthService.finishExternalSignin(authType, service, code, state)
    saveTokens(tokens)

    const user = await getUser()

    // Track login success in GTM dataLayer
    if (user) {
      const oktaSub = getOktaUserSub()
      trackEvent({
        event: AuthEvent.SigninSuccess,
        user_id: oktaSub ?? user.userId,
      })
      traceOtelAuthEvent(AuthEvent.SigninSuccess, oktaSub ?? user.userId)
    }

    return user
  }

  /**
   * Refresh auth tokens
   */
  async function refreshTokens() {
    const refreshToken = tokensStorage.getRefreshToken()
    if (refreshToken) {
      const tokens = await AuthService.refreshTokens(refreshToken)
      tokens && saveTokens(tokens)
    }
  }

  /**
   * Get current authenticated user details
   */
  async function getUser() {
    const user = await AuthService.getUser()
    const allowedFeatures = (user?.allowedFeatures ?? []) as Feature[]

    // We need to set features here, not waiting till they are loaded with config,
    // because waiting will cause race conditions for pages we're on, and we can encounter
    // forbidden error.
    allowedFeaturesStorage.setFeatures(allowedFeatures)
    delete user.allowedFeatures
    userStorage.saveUser(user)
    setUser(user)

    await config.loadConfiguration()

    // Rewrite features, obtained with config, by obtained with user, to avoid slitest chance
    // of race conditions or missmatch
    allowedFeaturesStorage.setFeatures(allowedFeatures)

    return user
  }

  /**
   * Signout
   */
  async function signout() {
    await AuthService.signout()

    // Track logout success in GTM dataLayer before clearing user state
    trackEvent({
      event: AuthEvent.SignoutSuccess,
    })
    traceOtelAuthEvent(AuthEvent.SignoutSuccess)

    // check if webpage is being used in an iframe
    const isInIframe = typeof window !== 'undefined' && window.self !== window.top

    if (isInIframe && config.ocm?.iframeLogoutUrl) {
      // Post log out event to parent
      window.parent.postMessage({ event: 'IFRAME_USER_LOGOUT_SSO' }, config.ocm.iframeLogoutUrl)
    }

    clearUserState()
  }

  /**
   * Clear user authentication related data
   */
  function clearUserState() {
    userStorage.clearUser()
    tokensStorage.clearAccessToken()
    tokensStorage.clearRefreshToken()
    allowedFeaturesStorage.clearFeatures()
    setUser(undefined)
  }

  /**
   * Request password reset
   */
  async function requestPasswordReset(email: string) {
    await AuthService.requestPasswordReset(email)
  }

  /**
   * Check if password reset token is valid
   */
  async function validatePasswordResetToken(token: string) {
    await AuthService.validatePasswordResetToken(token)
  }

  /**
   * Set new password
   */
  async function resetPassword(token: string, password: string, password_confirm: string) {
    await AuthService.resetPassword(token, password, password_confirm)
  }

  /**
   * Save tokens to some storage to use them later
   */
  function saveTokens(tokens: Tokens) {
    tokensStorage.saveAccessToken(tokens.access_token)
    tokensStorage.saveRefreshToken(tokens.refresh_token)
  }

  return {
    user,
    getUser,
    signin,
    signout,
    signup,
    signupInvite,
    signupFirstUser,
    confirmSignup,
    resendConfirmLink,
    requestPasswordReset,
    validatePasswordResetToken,
    resetPassword,
    refreshTokens,
    finishExternalSignin,
    clearUserState,
  }
}
